/**
 * BatchUploadModal - Multi-file upload for 360 skybox images
 *
 * Allows users to select multiple 360 images at once, upload them
 * with progress tracking, and create scenes from them.
 *
 * Workflow:
 * 1. User clicks "Batch Upload" button
 * 2. File picker opens for multiple image selection
 * 3. Upload progress shown for each file
 * 4. Scenes created with uploaded skyboxes
 * 5. Optional: Open Scene Map Editor to arrange and connect scenes
 */

import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import type { ExperienceScene, SkyboxConfig, TextureVariant } from '../src/types/config';
import { TextureOptimizer, type OptimizedVariant } from '../src/utils/TextureOptimizer';

// Generate unique ID using browser crypto API
function generateId(): string {
  return crypto.randomUUID();
}

export interface BatchUploadOptions {
  /** Current user ID */
  userId: string;
  /** Sequence/tour ID */
  sequenceId: string;
  /** Callback when all uploads complete */
  onComplete: (scenes: ExperienceScene[]) => void;
  /** Callback when user cancels */
  onCancel: () => void;
  /** Check if user has upload access */
  hasUploadAccess: boolean;
  /** Show upgrade modal for locked users */
  showUpgradeModal?: () => void;
}

interface UploadItem {
  id: string;
  file: File;
  customName: string; // Editable scene name
  status: 'pending' | 'uploading' | 'complete' | 'error';
  progress: number;
  url?: string;
  variants?: Record<string, TextureVariant>; // All optimized variants
  error?: string;
}

export class BatchUploadModal {
  private options: BatchUploadOptions;
  private modalElement: HTMLElement | null = null;
  private uploadItems: UploadItem[] = [];
  private isUploading = false;

  constructor(options: BatchUploadOptions) {
    this.options = options;
  }

  /**
   * Open the batch upload modal
   */
  open(): void {
    if (!this.options.hasUploadAccess) {
      this.options.showUpgradeModal?.();
      return;
    }

    this.injectStyles();
    this.createModal();
    this.setupEventListeners();

    // Animate in
    requestAnimationFrame(() => {
      this.modalElement?.classList.add('visible');
    });
  }

  /**
   * Close the modal
   */
  close(): void {
    if (this.modalElement) {
      this.modalElement.classList.remove('visible');
      setTimeout(() => {
        this.destroy();
      }, 300);
    }
  }

  private injectStyles(): void {
    if (document.getElementById('batch-upload-modal-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'batch-upload-modal-styles';
    style.textContent = `
      .batch-upload-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      .batch-upload-modal.visible {
        opacity: 1;
        visibility: visible;
      }
      .bum-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }
      .bum-content {
        position: relative;
        width: 90vw;
        max-width: 600px;
        max-height: 85vh;
        background: rgba(20, 20, 30, 0.95);
        backdrop-filter: blur(30px);
        -webkit-backdrop-filter: blur(30px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 24px;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        transform: scale(0.95) translateY(10px);
        transition: transform 0.3s ease;
      }
      .batch-upload-modal.visible .bum-content {
        transform: scale(1) translateY(0);
      }
      .bum-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 20px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }
      .bum-header h3 {
        margin: 0;
        font-size: 17px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.95);
      }
      .bum-close-btn {
        background: none;
        border: none;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        padding: 4px 8px;
        color: rgba(255, 255, 255, 0.7);
        border-radius: 6px;
        transition: background 0.2s ease;
      }
      .bum-close-btn:hover {
        background: rgba(255, 255, 255, 0.1);
      }
      .bum-body {
        flex: 1;
        padding: 20px;
        overflow-y: auto;
        min-height: 200px;
      }
      .bum-dropzone {
        border: 2px dashed rgba(255, 255, 255, 0.3);
        border-radius: 16px;
        padding: 40px 20px;
        text-align: center;
        cursor: pointer;
        transition: border-color 0.2s ease, background 0.2s ease;
      }
      .bum-dropzone:hover, .bum-dropzone.dragover {
        border-color: #007AFF;
        background: rgba(0, 122, 255, 0.1);
      }
      .bum-dropzone-icon {
        font-size: 48px;
        margin-bottom: 12px;
      }
      .bum-dropzone-text {
        color: rgba(255, 255, 255, 0.9);
        font-size: 16px;
        margin-bottom: 8px;
      }
      .bum-dropzone-hint {
        color: rgba(255, 255, 255, 0.5);
        font-size: 13px;
      }
      .bum-file-list {
        margin-top: 16px;
      }
      .bum-file-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 10px;
        margin-bottom: 8px;
      }
      .bum-file-preview {
        width: 48px;
        height: 48px;
        border-radius: 8px;
        object-fit: cover;
        background: rgba(0, 0, 0, 0.3);
      }
      .bum-file-info {
        flex: 1;
        min-width: 0;
      }
      .bum-file-name {
        color: rgba(255, 255, 255, 0.9);
        font-size: 14px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 4px;
      }
      .bum-file-progress {
        height: 4px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 2px;
        overflow: hidden;
      }
      .bum-file-progress-fill {
        height: 100%;
        background: #007AFF;
        transition: width 0.3s ease;
        border-radius: 2px;
      }
      .bum-file-progress-fill.complete {
        background: #34C759;
      }
      .bum-file-progress-fill.error {
        background: #FF3B30;
      }
      .bum-file-status {
        color: rgba(255, 255, 255, 0.5);
        font-size: 12px;
        margin-top: 4px;
      }
      .bum-file-status.complete {
        color: #34C759;
      }
      .bum-file-status.error {
        color: #FF3B30;
      }
      .bum-file-remove {
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.5);
        cursor: pointer;
        padding: 4px;
        font-size: 18px;
        border-radius: 4px;
        transition: color 0.2s ease;
      }
      .bum-file-remove:hover {
        color: #FF3B30;
      }
      .bum-file-name-input {
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 6px;
        padding: 6px 10px;
        color: rgba(255, 255, 255, 0.9);
        font-size: 13px;
        width: 100%;
        margin-bottom: 6px;
        outline: none;
        transition: border-color 0.2s ease;
      }
      .bum-file-name-input:focus {
        border-color: #007AFF;
      }
      .bum-file-name-input:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .bum-reorder-btns {
        display: flex;
        flex-direction: column;
        gap: 2px;
        margin-right: 4px;
      }
      .bum-reorder-btn {
        background: rgba(255, 255, 255, 0.1);
        border: none;
        color: rgba(255, 255, 255, 0.6);
        cursor: pointer;
        padding: 4px 6px;
        font-size: 10px;
        border-radius: 4px;
        transition: all 0.2s ease;
        line-height: 1;
      }
      .bum-reorder-btn:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.2);
        color: rgba(255, 255, 255, 0.9);
      }
      .bum-reorder-btn:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      .bum-file-order {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.4);
        margin-right: 8px;
        min-width: 20px;
        text-align: center;
      }
      .bum-footer {
        padding: 16px 20px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }
      .bum-footer-info {
        color: rgba(255, 255, 255, 0.6);
        font-size: 13px;
      }
      .bum-actions {
        display: flex;
        gap: 12px;
      }
      .bum-btn {
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        border: none;
      }
      .bum-btn-secondary {
        background: rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.15);
      }
      .bum-btn-secondary:hover {
        background: rgba(255, 255, 255, 0.15);
      }
      .bum-btn-primary {
        background: #007AFF;
        color: white;
      }
      .bum-btn-primary:hover:not(:disabled) {
        background: #0066DD;
      }
      .bum-btn-primary:disabled {
        background: rgba(255, 255, 255, 0.2);
        color: rgba(255, 255, 255, 0.4);
        cursor: not-allowed;
      }
      .bum-summary {
        padding: 16px;
        background: rgba(52, 199, 89, 0.1);
        border: 1px solid rgba(52, 199, 89, 0.3);
        border-radius: 12px;
        text-align: center;
        margin-top: 16px;
      }
      .bum-summary-icon {
        font-size: 32px;
        margin-bottom: 8px;
      }
      .bum-summary-text {
        color: #34C759;
        font-size: 15px;
        font-weight: 500;
      }
    `;
    document.head.appendChild(style);
  }

  private createModal(): void {
    this.modalElement = document.createElement('div');
    this.modalElement.className = 'batch-upload-modal';
    this.modalElement.innerHTML = `
      <div class="bum-backdrop"></div>
      <div class="bum-content">
        <div class="bum-header">
          <h3>Batch Upload 360 Images</h3>
          <button class="bum-close-btn" aria-label="Close">&times;</button>
        </div>
        <div class="bum-body">
          <div class="bum-dropzone">
            <div class="bum-dropzone-icon">📷</div>
            <div class="bum-dropzone-text">Drop 360 images here or click to browse</div>
            <div class="bum-dropzone-hint">Supports JPG, PNG, WebP (multiple files allowed)</div>
          </div>
          <input type="file" class="bum-file-input" multiple accept="image/*" style="display: none;">
          <div class="bum-file-list"></div>
        </div>
        <div class="bum-footer">
          <div class="bum-footer-info">
            <span class="bum-file-count">0 files selected</span>
          </div>
          <div class="bum-actions">
            <button class="bum-btn bum-btn-secondary bum-cancel-btn">Cancel</button>
            <button class="bum-btn bum-btn-primary bum-upload-btn" disabled>Upload All</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.modalElement);
  }

  private setupEventListeners(): void {
    if (!this.modalElement) return;

    const dropzone = this.modalElement.querySelector('.bum-dropzone') as HTMLElement;
    const fileInput = this.modalElement.querySelector('.bum-file-input') as HTMLInputElement;
    const uploadBtn = this.modalElement.querySelector('.bum-upload-btn') as HTMLButtonElement;
    const cancelBtn = this.modalElement.querySelector('.bum-cancel-btn') as HTMLButtonElement;
    const closeBtn = this.modalElement.querySelector('.bum-close-btn') as HTMLButtonElement;
    const backdrop = this.modalElement.querySelector('.bum-backdrop') as HTMLElement;

    // Dropzone click
    dropzone?.addEventListener('click', () => fileInput?.click());

    // Drag and drop
    dropzone?.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.classList.add('dragover');
    });

    dropzone?.addEventListener('dragleave', () => {
      dropzone.classList.remove('dragover');
    });

    dropzone?.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('dragover');
      const files = e.dataTransfer?.files;
      if (files) {
        this.addFiles(Array.from(files));
      }
    });

    // File input change
    fileInput?.addEventListener('change', () => {
      if (fileInput.files) {
        this.addFiles(Array.from(fileInput.files));
        fileInput.value = '';
      }
    });

    // Upload button
    uploadBtn?.addEventListener('click', () => this.startUpload());

    // Cancel/close
    cancelBtn?.addEventListener('click', () => {
      this.options.onCancel();
      this.close();
    });

    closeBtn?.addEventListener('click', () => {
      this.options.onCancel();
      this.close();
    });

    backdrop?.addEventListener('click', () => {
      if (!this.isUploading) {
        this.options.onCancel();
        this.close();
      }
    });

    // Escape key
    document.addEventListener('keydown', this.handleKeyDown);
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && !this.isUploading) {
      this.options.onCancel();
      this.close();
    }
  };

  private addFiles(files: File[]): void {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));

    for (const file of imageFiles) {
      // Check for duplicates
      if (this.uploadItems.some(item => item.file.name === file.name && item.file.size === file.size)) {
        continue;
      }

      // Generate initial scene name from filename
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const initialName = this.formatSceneName(baseName, this.uploadItems.length + 1);

      this.uploadItems.push({
        id: generateId(),
        file,
        customName: initialName,
        status: 'pending',
        progress: 0
      });
    }

    this.renderFileList();
    this.updateFooter();
  }

  private removeFile(id: string): void {
    this.uploadItems = this.uploadItems.filter(item => item.id !== id);
    this.renderFileList();
    this.updateFooter();
  }

  private renderFileList(): void {
    const listContainer = this.modalElement?.querySelector('.bum-file-list') as HTMLElement;
    if (!listContainer) return;

    if (this.uploadItems.length === 0) {
      listContainer.innerHTML = '';
      return;
    }

    const canEdit = !this.isUploading;
    const itemCount = this.uploadItems.length;

    listContainer.innerHTML = this.uploadItems.map((item, index) => {
      const statusClass = item.status === 'complete' ? 'complete' : item.status === 'error' ? 'error' : '';
      const statusText = item.status === 'pending' ? 'Ready to upload' :
                         item.status === 'uploading' ? `${item.progress}%` :
                         item.status === 'complete' ? 'Complete' :
                         item.error || 'Error';

      return `
        <div class="bum-file-item" data-id="${item.id}">
          ${canEdit && itemCount > 1 ? `
            <div class="bum-reorder-btns">
              <button class="bum-reorder-btn bum-move-up" data-id="${item.id}" ${index === 0 ? 'disabled' : ''} title="Move up">▲</button>
              <button class="bum-reorder-btn bum-move-down" data-id="${item.id}" ${index === itemCount - 1 ? 'disabled' : ''} title="Move down">▼</button>
            </div>
          ` : ''}
          <span class="bum-file-order">${index + 1}</span>
          <img class="bum-file-preview" src="${URL.createObjectURL(item.file)}" alt="${this.escapeHtml(item.file.name)}">
          <div class="bum-file-info">
            <input
              type="text"
              class="bum-file-name-input"
              data-id="${item.id}"
              value="${this.escapeHtml(item.customName)}"
              placeholder="Scene name"
              ${!canEdit ? 'disabled' : ''}
            >
            <div class="bum-file-progress">
              <div class="bum-file-progress-fill ${statusClass}" style="width: ${item.progress}%"></div>
            </div>
            <div class="bum-file-status ${statusClass}">${statusText}</div>
          </div>
          ${item.status === 'pending' && canEdit ? `
            <button class="bum-file-remove" data-id="${item.id}" title="Remove">&times;</button>
          ` : ''}
        </div>
      `;
    }).join('');

    // Add event handlers
    this.setupFileListEventHandlers(listContainer);
  }

  private setupFileListEventHandlers(listContainer: HTMLElement): void {
    // Remove button handlers
    listContainer.querySelectorAll('.bum-file-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.id;
        if (id) this.removeFile(id);
      });
    });

    // Name input handlers
    listContainer.querySelectorAll('.bum-file-name-input').forEach(input => {
      input.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        const id = target.dataset.id;
        if (id) {
          const item = this.uploadItems.find(i => i.id === id);
          if (item) {
            item.customName = target.value;
          }
        }
      });
    });

    // Move up button handlers
    listContainer.querySelectorAll('.bum-move-up').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.id;
        if (id) this.moveItem(id, -1);
      });
    });

    // Move down button handlers
    listContainer.querySelectorAll('.bum-move-down').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.id;
        if (id) this.moveItem(id, 1);
      });
    });
  }

  private moveItem(id: string, direction: -1 | 1): void {
    const index = this.uploadItems.findIndex(item => item.id === id);
    if (index === -1) return;

    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= this.uploadItems.length) return;

    // Swap items
    const temp = this.uploadItems[index];
    this.uploadItems[index] = this.uploadItems[newIndex];
    this.uploadItems[newIndex] = temp;

    this.renderFileList();
  }

  private updateFooter(): void {
    const countEl = this.modalElement?.querySelector('.bum-file-count');
    const uploadBtn = this.modalElement?.querySelector('.bum-upload-btn') as HTMLButtonElement;

    if (countEl) {
      countEl.textContent = `${this.uploadItems.length} file${this.uploadItems.length !== 1 ? 's' : ''} selected`;
    }

    if (uploadBtn) {
      uploadBtn.disabled = this.uploadItems.length === 0 || this.isUploading;
    }
  }

  private async startUpload(): Promise<void> {
    if (this.uploadItems.length === 0 || this.isUploading) return;

    this.isUploading = true;
    this.updateFooter();

    const storage = getStorage();
    const totalItems = this.uploadItems.length;

    // Upload each file with auto-optimization
    for (let itemIndex = 0; itemIndex < this.uploadItems.length; itemIndex++) {
      const item = this.uploadItems[itemIndex];
      if (item.status === 'complete') continue;

      item.status = 'uploading';
      this.renderFileList();

      try {
        // Step 1: Generate optimized variants (20% of progress per item)
        console.log(`[BatchUpload] Generating variants for: ${item.file.name}`);
        const variants = await TextureOptimizer.generateAllVariants(
          item.file,
          false, // Don't use KTX2 for batch uploads (faster)
          (variantName, progress) => {
            // Show variant generation progress (0-20%)
            item.progress = Math.round(progress * 0.2 / 5); // 5 variants, 20% total
            this.renderFileList();
          }
        );

        // Step 2: Upload each variant (80% of progress per item)
        const uploadedVariants: Record<string, TextureVariant> = {};
        const variantCount = variants.length;
        let uploadedCount = 0;

        for (const variant of variants) {
          const variantFile = new File(
            [variant.blob],
            `${item.id}-${variant.name}.jpg`,
            { type: 'image/jpeg' }
          );

          // Path must match storage rules: uploads/{userId}/{sequenceId}/{assetType}/{fileName}
          const storagePath = `uploads/${this.options.userId}/${this.options.sequenceId}/images/skybox_${item.id}_${variant.name}.jpg`;
          const storageRef = ref(storage, storagePath);

          const uploadTask = uploadBytesResumable(storageRef, variantFile);

          await new Promise<void>((resolve, reject) => {
            uploadTask.on('state_changed',
              (snapshot) => {
                // Progress: 20% base + variant upload progress (80% total split across variants)
                const baseProgress = 20 + (uploadedCount * 80 / variantCount);
                const variantProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * (80 / variantCount);
                item.progress = Math.round(baseProgress + variantProgress);
                this.renderFileList();
              },
              (error) => {
                reject(error);
              },
              async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                uploadedVariants[variant.name] = {
                  url,
                  format: variant.format,
                  resolution: `${variant.width}x${variant.height}`,
                  fileSize: variant.size
                };
                uploadedCount++;
                console.log(`[BatchUpload] Uploaded ${variant.name}: ${url}`);
                resolve();
              }
            );
          });
        }

        // Set the primary URL to desktop or fallback for backward compatibility
        item.url = uploadedVariants.desktop?.url || uploadedVariants.fallback?.url;
        item.variants = uploadedVariants;
        item.status = 'complete';
        item.progress = 100;
        this.renderFileList();
        console.log(`[BatchUpload] ✓ Completed ${item.file.name} with ${variantCount} variants`);

      } catch (error) {
        item.status = 'error';
        item.error = error instanceof Error ? error.message : 'Upload failed';
        console.error(`[BatchUpload] Failed:`, error);
        this.renderFileList();
      }
    }

    this.isUploading = false;

    // Check if all uploads completed
    const completedItems = this.uploadItems.filter(item => item.status === 'complete' && item.url);

    if (completedItems.length > 0) {
      // Create scenes from uploaded images
      const scenes = this.createScenesFromUploads(completedItems);
      this.showCompletionSummary(completedItems.length);

      // Wait a moment for user to see the summary
      setTimeout(() => {
        this.options.onComplete(scenes);
        this.close();
      }, 1500);
    }
  }

  private createScenesFromUploads(items: UploadItem[]): ExperienceScene[] {
    return items.map((item, index) => {
      // Use custom name if set, otherwise fall back to formatted filename
      const sceneName = item.customName.trim() || `Scene ${index + 1}`;

      const skybox: SkyboxConfig = {
        type: 'image',
        url: item.url!,
        // Include all optimized variants for multi-device support
        variants: item.variants as any
      };

      const scene: ExperienceScene = {
        id: item.id,
        name: sceneName,
        skybox,
        hotspots: [],
        models: [],
        planes: [],
        lights: []
      };

      return scene;
    });
  }

  private formatSceneName(baseName: string, index: number): string {
    // Clean up filename to make a nice scene name
    let name = baseName
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Capitalize first letter of each word
    name = name.replace(/\b\w/g, c => c.toUpperCase());

    // If name is generic or empty, use "Scene X"
    if (!name || /^(img|image|photo|pic|dsc|p|photo)/i.test(name)) {
      return `Scene ${index}`;
    }

    return name;
  }

  private showCompletionSummary(count: number): void {
    const body = this.modalElement?.querySelector('.bum-body') as HTMLElement;
    if (body) {
      body.innerHTML = `
        <div class="bum-summary">
          <div class="bum-summary-icon">✅</div>
          <div class="bum-summary-text">${count} scene${count !== 1 ? 's' : ''} created successfully!</div>
        </div>
      `;
    }

    // Hide footer buttons
    const footer = this.modalElement?.querySelector('.bum-footer') as HTMLElement;
    if (footer) {
      footer.style.display = 'none';
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private destroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.modalElement?.remove();
    this.modalElement = null;
    this.uploadItems = [];
  }
}

/**
 * Helper function to open the batch upload modal
 */
export function openBatchUploadModal(options: BatchUploadOptions): BatchUploadModal {
  const modal = new BatchUploadModal(options);
  modal.open();
  return modal;
}
